/**
 * Choosing a chord with your thumbs.
 *
 * ## The unit of design is the tap
 *
 * At the piano the input device is a piano and there is nothing to design. On a train the
 * input device is one hand on a phone, on a bus, badly — so the whole picker is measured in
 * taps rather than in screens. Every chord in the primary vocabulary is exactly two: a
 * root, then a quality, which commits immediately. There is no confirm button, because a
 * confirm button would make every chord three taps in exchange for protecting against a
 * mistake that costs one tap on a history node to undo.
 *
 * Three zones, in the order someone actually reaches for them:
 *
 *  1. **Recents** — where you have just been, latest first, and on a fresh session the keys
 *     this player tends to live in. One tap. Doodling is repetitive and this is the row
 *     that admits it.
 *  2. **Root × quality** — the workhorse. Sixteen qualities, eight of them behind a
 *     disclosure, because the eight in front cover everything most people play and a grid
 *     of sixteen is a grid nobody scans.
 *  3. **Preview and typing** — for slash basses, alterations, and anything the grid does
 *     not have a cell for. The escape hatch is deliberately last: it is the slowest path
 *     and the one fewest people need.
 *
 * ## No new theory
 *
 * A root and a quality are assembled into a symbol string and handed to the *existing*
 * `parseChordSymbol` → `toCanonical` → `respell` pipeline — the same one the correction
 * sheet and manual entry use. There is no second table of chord formulas here, and there
 * must never be one: two tables would eventually disagree, and the day they did, a picked
 * chord would stop matching the branch card that offered it.
 */

import { useMemo, useState } from 'react';
import {
  isParseError,
  noteName,
  parseChordSymbol,
  respell,
  spellPitchClass,
  toCanonical,
  type CanonicalChord,
  type KeyContext,
  type PitchClass,
  type SessionNode,
} from '@hc/shared';
import { BottomSheet } from '../system/BottomSheet.js';
import { ChordSymbol } from '../system/ChordSymbol.js';
import { ChordTextEntry, readChordEntry } from '../system/ChordTextEntry.js';
import { MiniKeyboard } from '../system/MiniKeyboard.js';

/** One cell in the quality grid. `suffix` is fed to the parser verbatim. */
export interface PickerQuality {
  label: string;
  suffix: string;
}

/**
 * The eight that are always showing.
 *
 * Chosen for coverage of what people actually play rather than for symmetry: the four
 * triads and the four sevenths that appear in pop, jazz and hymnody alike. `m7♭5` earns
 * its place over `aug` because it is half of every minor ii–V and nobody can spell it
 * quickly by hand.
 */
export const PRIMARY_QUALITIES: readonly PickerQuality[] = Object.freeze([
  { label: 'maj', suffix: '' },
  { label: 'min', suffix: 'm' },
  { label: '7', suffix: '7' },
  { label: 'maj7', suffix: 'maj7' },
  { label: 'min7', suffix: 'm7' },
  { label: 'm7♭5', suffix: 'm7b5' },
  { label: 'dim', suffix: 'dim' },
  { label: 'sus4', suffix: 'sus4' },
]);

/** The second eight, one disclosure away. Still two taps once the row is open. */
export const MORE_QUALITIES: readonly PickerQuality[] = Object.freeze([
  { label: '6', suffix: '6' },
  { label: 'm6', suffix: 'm6' },
  { label: 'sus2', suffix: 'sus2' },
  { label: 'aug', suffix: 'aug' },
  { label: 'dim7', suffix: 'dim7' },
  { label: 'add9', suffix: 'add9' },
  { label: '9', suffix: '9' },
  { label: 'm9', suffix: 'm9' },
]);

const ROOTS: readonly PitchClass[] = Object.freeze([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11]);

/** Enough to be useful in a glance, few enough to stay one row on a phone. */
export const RECENTS_LIMIT = 8;

/**
 * What the recents row falls back to before this player has any history.
 *
 * The keys most Western music sits in, and only a hint — `preferenceHints.commonKeys`
 * replaces it the moment there is evidence. See `state/preferences.ts` for why the
 * personalisation here is only ever allowed to lean.
 */
export const DEFAULT_COMMON_KEYS: readonly PitchClass[] = Object.freeze([0, 7, 5, 9, 2]);

/**
 * Assemble a symbol and read it back through the parser.
 *
 * Returns `null` only if a root-plus-suffix from the tables above fails to parse, which
 * would be a bug in this file rather than anything the player did — so the cell simply
 * does nothing instead of showing them an error for a button we shipped.
 */
export function pickedChord(
  rootPc: PitchClass,
  suffix: string,
  key: KeyContext,
): CanonicalChord | null {
  const spec = parseChordSymbol(noteName(spellPitchClass(rootPc, key)) + suffix);
  if (isParseError(spec)) return null;
  return respell(toCanonical(spec), key);
}

/**
 * The recents row: distinct chords from the path, latest first, padded with tonic triads
 * of the keys this player uses.
 *
 * Deduplicated on the display symbol rather than on the pitch-class set, because a row
 * offering both "C" and "C" is noise while a row offering "C" and "C/E" is two genuinely
 * different chords to choose between.
 */
export function recentChords(
  path: readonly SessionNode[],
  commonKeys: readonly PitchClass[],
  key: KeyContext,
): CanonicalChord[] {
  const chords: CanonicalChord[] = [];
  const seen = new Set<string>();

  const add = (chord: CanonicalChord | null): void => {
    if (!chord || seen.has(chord.symbol) || chords.length >= RECENTS_LIMIT) return;
    seen.add(chord.symbol);
    chords.push(chord);
  };

  for (let index = path.length - 1; index >= 0; index -= 1) {
    // Respelled against the key as it stands now, not as it stood then: the row is a set
    // of things to play next, and next happens in the current key.
    add(respell(path[index]!.chord, key));
  }
  for (const pc of commonKeys) add(pickedChord(pc, '', key));

  return chords;
}

export interface ChordPickerProps {
  open: boolean;
  onClose: () => void;
  /** The selection funnel. Committing also closes the sheet. */
  onSelect: (chord: CanonicalChord) => void;
  keyContext: KeyContext;
  /** Drives the recents row. */
  path: readonly SessionNode[];
  /** `preferenceHints.commonKeys`, when the host has them. */
  commonKeys?: readonly PitchClass[];
  title?: string;
}

export function ChordPicker({
  open,
  onClose,
  onSelect,
  keyContext,
  path,
  commonKeys = DEFAULT_COMMON_KEYS,
  title = 'Any chord',
}: ChordPickerProps): React.JSX.Element {
  const [root, setRoot] = useState<PitchClass | null>(null);
  const [showMore, setShowMore] = useState(false);
  const [typed, setTyped] = useState('');

  const recents = useMemo(
    () => recentChords(path, commonKeys, keyContext),
    [path, commonKeys, keyContext],
  );

  const rootNames = useMemo(
    () => ROOTS.map((pc) => noteName(spellPitchClass(pc, keyContext))),
    [keyContext],
  );

  // The preview is the chosen root as a plain major triad — what the next tap will modify.
  // Read back through `readChordEntry` so the notes line is produced by exactly the code
  // that produces it under the text field, rather than by a second spelling routine.
  const preview = useMemo(
    () => readChordEntry(root === null ? '' : rootNames[root]!, keyContext),
    [root, rootNames, keyContext],
  );

  const commit = (chord: CanonicalChord | null): void => {
    if (!chord) return;
    onSelect(chord);
    setRoot(null);
    setShowMore(false);
    setTyped('');
    onClose();
  };

  return (
    <BottomSheet open={open} onClose={onClose} title={title} testId="hc-chord-picker">
      <div className="hc-picker">
        <section className="hc-picker__zone">
          <h3 className="hc-picker__label">Recent</h3>
          <ul className="hc-picker__recents" data-testid="hc-picker-recents">
            {recents.map((chord) => (
              <li key={chord.symbol}>
                <button
                  type="button"
                  className="hc-picker__chip"
                  data-testid="hc-picker-recent"
                  data-symbol={chord.symbol}
                  onClick={() => commit(chord)}
                >
                  <ChordSymbol symbol={chord.symbol} />
                </button>
              </li>
            ))}
          </ul>
        </section>

        <section className="hc-picker__zone">
          <h3 className="hc-picker__label">Root</h3>
          <div className="hc-picker__roots" role="group" aria-label="Root">
            {ROOTS.map((pc) => (
              <button
                key={pc}
                type="button"
                className={`hc-picker__root${root === pc ? ' is-selected' : ''}`}
                data-testid="hc-picker-root"
                data-pc={pc}
                aria-pressed={root === pc}
                onClick={() => setRoot(pc)}
              >
                <ChordSymbol symbol={rootNames[pc]!} />
              </button>
            ))}
          </div>
        </section>

        <section className="hc-picker__zone">
          <h3 className="hc-picker__label">
            Quality
            {root === null ? <span className="hc-picker__hint"> — pick a root first</span> : null}
          </h3>
          <div className="hc-picker__qualities" role="group" aria-label="Quality">
            {PRIMARY_QUALITIES.map((quality) => (
              <QualityCell
                key={quality.label}
                quality={quality}
                root={root}
                keyContext={keyContext}
                onCommit={commit}
              />
            ))}
          </div>

          <button
            type="button"
            className="hc-picker__more"
            data-testid="hc-picker-more"
            aria-expanded={showMore}
            onClick={() => setShowMore((current) => !current)}
          >
            {showMore ? 'fewer' : 'more…'}
          </button>

          {showMore ? (
            <div
              className="hc-picker__qualities"
              data-testid="hc-picker-more-qualities"
              role="group"
              aria-label="More qualities"
            >
              {MORE_QUALITIES.map((quality) => (
                <QualityCell
                  key={quality.label}
                  quality={quality}
                  root={root}
                  keyContext={keyContext}
                  onCommit={commit}
                />
              ))}
            </div>
          ) : null}
        </section>

        <section className="hc-picker__zone">
          <p className="hc-picker__preview" data-testid="hc-picker-preview">
            {preview.chord ? (
              <>
                <MiniKeyboard
                  pitchClasses={preview.chord.pitchClasses}
                  bassPc={preview.chord.bassPc}
                  label={`${preview.chord.symbol} notes`}
                />
                <span className="hc-picker__preview-symbol">
                  <ChordSymbol symbol={preview.chord.symbol} />
                </span>
                <span className="hc-picker__preview-notes">{preview.message}</span>
              </>
            ) : (
              <span className="hc-picker__preview-notes">Two taps: a root, then a quality.</span>
            )}
          </p>

          <ChordTextEntry
            value={typed}
            onChange={setTyped}
            onCommit={commit}
            keyContext={keyContext}
            label="Or type anything"
            placeholder="e.g. Cm7/E♭"
          />
        </section>
      </div>
    </BottomSheet>
  );
}

/**
 * One quality cell.
 *
 * The chord is built here rather than in the click handler so the cell can disable itself
 * when the combination does not parse — which, given the tables above, means never, and
 * that is the point: if a cell ever goes dead it is visible immediately rather than
 * swallowing taps.
 */
function QualityCell({
  quality,
  root,
  keyContext,
  onCommit,
}: {
  quality: PickerQuality;
  root: PitchClass | null;
  keyContext: KeyContext;
  onCommit: (chord: CanonicalChord | null) => void;
}): React.JSX.Element {
  const chord = root === null ? null : pickedChord(root, quality.suffix, keyContext);
  return (
    <button
      type="button"
      className="hc-picker__quality"
      data-testid="hc-picker-quality"
      data-suffix={quality.suffix}
      data-symbol={chord?.symbol}
      disabled={chord === null}
      onClick={() => onCommit(chord)}
    >
      {quality.label}
    </button>
  );
}

export default ChordPicker;
